import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  InputAdornment,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { ApiApplication } from '../../../../api/hexa/ApiApplication';
import ConsumerDetail from './consumer/ConsumerDetail';
import SaveButtonComponent from '../../../components/SaveButton';
import AppCustomDopList from './app_custom_dop/AppCustomDopList';
import React from 'react';
import ConfirmContent from '../../../components/ConfirmContent';
import { Save } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { BreadcrumbsTitleContext } from '../../../../wrappers/AppWrapper';
import {
  Application,
  ApplicationDetailDialogProps,
  ApplicationFormProps,
} from '../../../../api/hexa/models/ApplicationModels';

const DEFAULT_FORM = {
  name: '',
  active: false,
  created: '',
  consumer_key: '',
  consumer_secret: '',
};

const ApplicationDetail = (props: ApplicationDetailDialogProps) => {
  const navigate = useNavigate();
  const [state, setState] = React.useState(true);
  const formRef = React.useRef<HTMLDivElement>(null);
  const confirmRef = React.useRef<HTMLDivElement>(null);
  const nodeRef = state ? formRef : confirmRef;
  const [error, setError] = useState({
    open: false,
    message: '',
  });
  const { setApplicationName } = useContext(BreadcrumbsTitleContext);
  const { open, onClose, applicationListUpdate, isDialog } = props;
  const [initialFormValue, setInitialFormValue] =
    useState<ApplicationFormProps>(DEFAULT_FORM);
  const [isFormUnchanged, setIsFormUnchanged] = useState(true);
  const [isLoading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const { customerId, applicationId } = useParams();
  const {
    getValues,
    control,
    reset,
    formState: { isValid },
  } = useForm<ApplicationFormProps>({
    defaultValues: DEFAULT_FORM,
    mode: 'onChange',
  });
  const formValues = useWatch({ control });

  /**
   * Fetch application from the API, updatethe form with the fetched data
   */
  const fetchApplication = useCallback(async () => {
    if (!customerId || !applicationId) return;
    setLoading(true);
    try {
      const application = await ApiApplication.show(customerId, applicationId);
      setApplicationName(application.name);
      localStorage.setItem('applicationName', application.name);
      const formValues = {
        name: application.name ?? '',
        active: application.active ?? false,
        created:
          new Date(application.created).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }) ?? '',
        consumer_key: '',
        consumer_secret: '',
      };
      reset(formValues);
      setInitialFormValue(formValues);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching application: 
    ${error.response?.data.error ?? 'Error fetching application'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, applicationId, setApplicationName, reset]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  /**
   * Close the dialog and update the application list if the dialog
   * is opened from the application list
   */
  const handleClose = useCallback(() => {
    if (onClose && applicationListUpdate) {
      onClose();
      applicationListUpdate();
    }
  }, [applicationListUpdate, onClose]);

  /**
   * Check if the form values have changed, used to enable/disable save button
   */
  useEffect(() => {
    setIsFormUnchanged(
      JSON.stringify(formValues) === JSON.stringify(initialFormValue)
    );
  }, [formValues, initialFormValue]);

  /**
   * Submit the application form data to the API, if triggered from the creation dialog
   * navigate to the detail of new app, otherwise it updates the existing one and updates
   * the form with the new values
   */
  const onSubmit = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const values: ApplicationFormProps = getValues();
      const data: Partial<Application> = {
        name: values.name,
        active: values.active,
      };
      if (!applicationId) {
        let application = await ApiApplication.create(customerId, data);
        handleClose();
        navigate(`./applications/${application.id}`);
        window.scrollTo(0, 0);
      } else {
        await ApiApplication.update(customerId, applicationId, data);
        setInitialFormValue(values);
      }
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} creating or updating the application: 
    ${error.response?.data.error ?? 'Error creating or updating the application'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [applicationId, customerId, getValues, handleClose, navigate]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  const FormDetail = (
    <>
      <CardContent sx={{ overflowY: 'unset' }}>
        <Stack
          sx={{
            display: 'grid',
            gap: '1rem',
            marginBottom: '1rem',
          }}
        >
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'applications.detail.custom_dop.name_required',
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                sx={{ mb: 2, gridColumn: '1', gridRow: '1' }}
                label={t('applications.detail.name')}
                required
                error={!!error?.message}
                helperText={error ? t(`${error.message}`) : ''}
              />
            )}
          />
          <Controller
            name={'active'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <TextField
                  sx={{ mb: 2, gridColumn: '2', gridRow: '1' }}
                  label={t('applications.detail.status')}
                  value={value ? 'Active' : 'Inactive'}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Switch
                          size="small"
                          checked={!!value}
                          onChange={onChange}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          />

          {!isDialog && (
            <Controller
              name="created"
              control={control}
              render={({ field }) => (
                <TextField
                  InputProps={{
                    readOnly: true,
                  }}
                  {...field}
                  sx={{ mb: 2, gridColumn: '1', gridRow: '2' }}
                  label={t('applications.detail.created')}
                />
              )}
            />
          )}
        </Stack>
      </CardContent>
    </>
  );

  const DetailContent = (
    <Card
      component="form"
      sx={{
        borderRadius: '0.5rem !important',
        paddingX: '1.5rem',
      }}
    >
      <CardHeader
        title={
          <Typography variant={isDialog ? 'h4' : 'h3'}>
            {t('applications.detail.title')}
          </Typography>
        }
      />
      {!isDialog && (
        <>
          <Stack direction="row" pr={'1rem'} justifyContent="flex-end">
            <SaveButtonComponent
              isLoading={isLoading}
              isFormUnchanged={isFormUnchanged}
              isValid={isValid}
              onSubmit={onSubmit}
            ></SaveButtonComponent>
          </Stack>
          {FormDetail}
        </>
      )}
      {isDialog && (
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={state.toString()}
            nodeRef={nodeRef}
            addEndListener={(done: any) => {
              if (nodeRef?.current) {
                nodeRef.current.addEventListener('transitionend', done, false);
              }
            }}
            classNames="fade"
          >
            <div ref={nodeRef} className="button-container">
              {state ? (
                <>
                  <Stack direction="row" pr={'1rem'} justifyContent="flex-end">
                    <LoadingButton
                      loading={isLoading}
                      disabled={isFormUnchanged || !isValid}
                      variant="contained"
                      color="primary"
                      onSubmit={onSubmit}
                      startIcon={<Save />}
                      onClick={() => setState((state) => !state)}
                    >
                      {t('save')}
                    </LoadingButton>
                  </Stack>
                  {FormDetail}
                </>
              ) : (
                <>
                  <ConfirmContent
                    onSaveClick={onSubmit}
                    onDiscardClick={() => setState((state) => !state)}
                  />
                </>
              )}
            </div>
          </CSSTransition>
        </SwitchTransition>
      )}

      {applicationId && applicationId !== 'new' && (
        <>
          <AppCustomDopList
            customerId={customerId ?? ''}
            applicationId={applicationId ?? ''}
          ></AppCustomDopList>
          <ConsumerDetail
            customerId={customerId ?? ''}
            applicationId={applicationId}
          />
        </>
      )}
    </Card>
  );

  return (
    <>
      {isDialog && (
        <Dialog
          onClose={handleClose}
          open={open ?? true}
          fullWidth
          maxWidth="md"
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '0.5rem',
              padding: '1rem',
              boxShadow: 'none',
            },
          }}
        >
          {DetailContent}
        </Dialog>
      )}
      {!isDialog && <Container>{DetailContent}</Container>}
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ApplicationDetail;
